import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

export default function ForgotPassword() {
  const [email, setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]     = useState(false);
  const [message, setMessage] = useState('');
  const [resetUrl, setResetUrl] = useState(''); // shown when email not configured
  const [error, setError]   = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await api.forgotPassword(email);
      setMessage(res.message);
      if (res.resetUrl) setResetUrl(res.resetUrl); // dev mode
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  const inputBase = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 12, padding: '10px 14px',
    fontSize: 14, color: '#fff', width: '100%', outline: 'none',
    transition: 'border-color 160ms ease',
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#08070f' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(94,14,215,0.10) 0%, transparent 70%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
        className="w-full max-w-md relative"
      >
        <div className="flex flex-col items-center mb-10">
          <img src="/logo.png" alt="Syenovix" className="w-14 h-14 rounded-2xl object-cover mb-4" style={{ filter: 'drop-shadow(0 0 10px rgba(94,14,215,0.5))' }} />
          <h1 className="text-xl font-bold tracking-widest uppercase text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Reset Password</h1>
        </div>

        <div className="rounded-[1.75rem] p-[1px]" style={{ background: 'linear-gradient(135deg, rgba(94,14,215,0.4), rgba(255,255,255,0.06))' }}>
          <div className="rounded-[1.7rem] p-8" style={{ background: '#0f0d1a' }}>
            {done ? (
              <div className="text-center py-4">
                <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: '#22c55e' }} />
                <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>{message}</p>
                {resetUrl && (
                  <div className="mt-4 p-3 rounded-xl text-left" style={{ background: 'rgba(94,14,215,0.12)', border: '1px solid rgba(94,14,215,0.25)' }}>
                    <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#c4a0ff' }}>Reset Link (email not configured):</p>
                    <a href={resetUrl} className="text-xs break-all" style={{ color: '#c4a0ff' }}>{resetUrl}</a>
                  </div>
                )}
                <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm mt-5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  <ArrowLeft size={13} /> Back to Login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Enter your admin email address and we'll send you a link to reset your password.
                </p>
                <div>
                  <label className="block text-[11px] font-semibold tracking-[0.1em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>Email</label>
                  <input
                    type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="admin@syenovix.com"
                    style={inputBase}
                    onFocus={e => e.target.style.borderColor = ACCENT}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.10)'}
                  />
                </div>
                {error && <p className="text-xs text-red-400 bg-red-400/10 px-3 py-2 rounded-lg">{error}</p>}
                <motion.button
                  type="submit"
                  whileTap={{ scale: 0.97 }}
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl text-sm font-bold uppercase text-white"
                  style={{ background: ACCENT, opacity: loading ? 0.7 : 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </motion.button>
                <Link to="/admin" className="flex items-center justify-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  <ArrowLeft size={12} /> Back to Login
                </Link>
              </form>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
